import React from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '../../store/reducer';
import { alpha, Box, Grid, LinearProgress, SxProps, Theme, ToggleButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import moment from 'moment';
import { addLineupEntry, removeMatchupsFilter, setAdditionalOptimizerLegs, setCurrentEntry, setIsEntryView, setMatchups, setMatchupsFilter, setOfferFilters, setSelectedEntry } from './store/slice';
import { IParlayOutcome, IShopOutcome, Leagues, OptimizerEntry } from './store/types';
import RefreshIcon from "@mui/icons-material/Refresh";
import ScrollableChips from './ScrollableChips';
import OSDataGrid from '../common';
import { createOptimizerMobileColumns } from './createOptimizerMobileColumns';
import { createOptimizerDesktopColumns } from './createOptimizerDesktopColumns';
import MatchupsMultiSelect from './MatchupsMultiSelect';
import { APIConstants } from '../utils/api/constants';

const OutcomesTableWrapper = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = React.useState(false);



  const { authenticated } = useSelector((state: IRootState) => state.common);
  const { optimizerState: { matchups, additionalLegs, entries, selectedDraftGroup, selectedEntry, maxLegs, matchupsFilter, offersFilter }, filters } = useSelector((state: IRootState) => state.pick6);
  const { league } = filters;
  const currentEntry = entries.find(x => x.id === selectedEntry) ?? entries[0];
  const exposureCounts = new Map<string, number>();

  const userAuthenticated =  authenticated.status === APIConstants.SUCCEEDED && authenticated.data.result;

  React.useEffect(() => {
    const fetchMatchupData = async () => {
      const matchups = await getMatchups();
      if (matchups) {
        dispatch(setMatchups(matchups));
      }
    };
    fetchMatchupData();
  }, [selectedDraftGroup, dispatch]);


  const firstRender = React.useRef(true);

  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    getOutcomes();
  }, [filters, matchups, matchupsFilter]);

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 25,
    page: 0,
  });

  for (const e of entries.filter(x => x.outcomes !== undefined)) {
    for (const o of e?.outcomes) {
      if (exposureCounts.has(o.playerId)) {
        const cur = exposureCounts.get(o.playerId);
        exposureCounts.set(o.playerId, cur ? cur + 1 : 1);
      } else {
        exposureCounts.set(o.playerId, 1);
      }
    }
  }

  // Filter the outcomes by matchup. This is done by using the matchup Id which is the same as the outcome event Id
  var ids = matchupsFilter.map((value) => value.id);
  var gridData = additionalLegs?.outcomes ?? [];
  if (matchupsFilter.length) {
    gridData = gridData.filter((value) => ids.includes(value.eventId));
  }
  if (selectedDraftGroup?.competitionIds) {
    gridData = gridData.filter((value) => selectedDraftGroup?.competitionIds.includes(parseInt(value.sportsbookEventId)));
  }

  let offers: Array<string> = [];
  if (additionalLegs?.outcomes?.length) {
    let distinctOffers = new Set(
      additionalLegs.outcomes.map((value) => value.offerName),
    );
    offers = Array.from(distinctOffers);
  }

  if (offersFilter.length) {
    gridData = gridData.filter((value) =>
      offersFilter.includes(value.offerName),
    );
  }

  const getMatchups = async () => {
    //Use the ISO8601 format here. This will handle either the ISO8601 or the timeoffset format that DK Pick6 sends. 
    //The offset format contains a + sign which we then have to encode and decode for api calls which is annoying so convert it here to an easier format.
    const start = moment(selectedDraftGroup?.minStartTime);
    const end = moment(selectedDraftGroup?.maxStartTime)
    return await fetch(`https://oddsshopper-ui.vercel.app/api/matchups/${league}?startDate=${start.toISOString()}&endDate=${end.toISOString()}&padEnd=false`).then((res) =>
      res.json(),
    );
  };

  const _setOfferFilters = (filters: string[]) => {
    dispatch(setOfferFilters(filters));
  };




  const getOutcomes = async () => {
    const filterTable = async () => {
      var eventIds = matchups?.map(m => m.id).join(',') ?? '';
      const queryParams = new URLSearchParams({
        leagueCode: filters.league as string,
        //selectedLegs: data.selectedLegs,
        isUserPremium: userAuthenticated,
        operator: "Pick6",
        currentPage: "1",
        sortBy: "win",
        selectedLegs: "",
        offers: filters.offers?.map((x) => x.id).join(",") ?? "",
        events: eventIds,
        accesstoken: 'stokasticTools',
      });
      setLoading(true);
      const url = `https://oddsshopper-ui.vercel.app/api/optimizer/outcomes?${queryParams}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        dispatch(setAdditionalOptimizerLegs(data));
      } else {
        console.error("Failed to fetch optimizer outcomes");
      }
      setLoading(false);
    };
    if (userAuthenticated) {
      filterTable();
    }
  };

  const getType = (o: IShopOutcome): "Add" | "Swap" | "Remove" => {
    let selectedLegs = currentEntry?.outcomes;

    if (selectedLegs?.find((leg) => leg.playerId === o.playerId && leg.outcomeId !== o.outcomeId)) {
      return "Swap";
    }

    if (selectedLegs?.find((leg) => leg.outcomeId === o.outcomeId)) {
      return "Remove";
    }

    return "Add";
  };

  const handleLegClick = (o: IParlayOutcome) => {
    let _entry = currentEntry;
    let selectedLegs = [...(_entry?.outcomes ?? [])];
    const selectedPlayerIndex: number = selectedLegs.findIndex((leg) => leg.playerId === o.playerId);
    const selectedPlayer: IShopOutcome | undefined = selectedPlayerIndex !== -1 ? selectedLegs[selectedPlayerIndex] : undefined;
    const selectedOutcome: IShopOutcome | undefined = selectedLegs.find((leg) => leg.outcomeId === o.outcomeId);
    const currentLeague: string | undefined = selectedPlayer?.leagueCode ?? selectedOutcome?.leagueCode ?? selectedLegs[0]?.leagueCode;
    // If there is no existing entry or the player being added is from a different league than the current entry then create a new entry.
    // Pick6 only allows players from the same league in an entry.
    // By default the use starts with zero entries so if they being building one without generating one is created.
    if (!_entry || currentLeague && currentLeague !== o.leagueCode) {
      _entry = { id: entries.length, outcomes: [o] } as OptimizerEntry;
      dispatch(addLineupEntry(_entry));
      dispatch(setSelectedEntry(_entry.id));
      dispatch(setIsEntryView(true));
      return;
    }

    // Add Player
    if (selectedLegs.length < maxLegs && !selectedPlayer && !selectedOutcome && (!currentLeague || currentLeague === o.leagueCode)) {
      dispatch(setCurrentEntry({ ..._entry, outcomes: [...selectedLegs, o] }));
      return;
    }

    // Remove Player
    if (selectedLegs.length && selectedOutcome?.outcomeId === o.outcomeId) {
      dispatch(setCurrentEntry({ ..._entry, outcomes: [...selectedLegs.filter((leg) => leg.outcomeId !== o.outcomeId)] }));
      return;
    }

    // Swap Player
    if (selectedLegs.length && selectedPlayer?.outcomeId !== o.outcomeId && selectedPlayer?.playerId === o.playerId) {
      selectedLegs.splice(selectedPlayerIndex, 1, o);
      dispatch(setCurrentEntry({ ..._entry, outcomes: selectedLegs }));
      return;
    }
  };


  const NoRowsOverlay = (leagues: Leagues[]) => {
    if (userAuthenticated) {
      return (
        <Box display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'center'} textAlign={'center'} height={'100%'} color='white'>
          <Typography>
            No Bets Available, please adjust your filters and try again.
          </Typography>
        </Box>
      );
    }
    else {
      //BDK FUTURE Make this CTA a component when we do v2 of the checkout flow
      return (
        <Box style={{ width: '100%', margin: 'auto', maxWidth: '430px', padding: '10px', flexDirection: 'column' }}>
          <Box style={{ padding: '10px 15px', borderRadius: 10, borderLeft: '5px #005CCE solid', borderTop: '1px #005CCE solid', borderRight: '5px #005CCE solid', borderBottom: '1px #005CCE solid', flexDirection: 'column', gap: '8px', display: 'flex' }}>
            {/* <Box style={{ justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex' }}>
                <img style={{ height: '25px' }} src='https://awesemostorage.blob.core.windows.net/assets/landing/odds-shopper-logo-inline-white.svg' />
                <SubscribeButton subscribeType="tableOverlay" />
              </Box> */}
            <Box style={{ flexDirection: 'column', gap: '4px', display: 'flex' }}>
              <span style={{ textAlign: 'center', color: theme.palette.common.white, fontSize: 18, fontWeight: '600', }}>Level Up Your Pickem Plays with Pick’Em All-Access!</span>
            </Box>
          </Box>
        </Box>
      );
    }
  }

  const mobileStyles: SxProps<Theme> = {
    border: "none",
    "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
      outline: "none !important",
    },
    "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
    {
      outline: "none !important",
    },
    "& .MuiDataGrid-row:hiddenBet": {
      opacity: 0.5,
      backgroundColor: "rgba(0, 0, 0, 1) !important",
    },
    "& .hiddenBet": {
      opacity: 0.5,
      backgroundColor: "rgba(0, 0, 0, 1) !important",
    },
    "& .MuiDataGrid-cell": {
      padding: 0,
      "&:focus": {
        outline: "none",
      },
    },
    "& .MuiDataGrid-withBorderColor": {
      border: "none",
    },
    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
      width: "0.2em",
    },
    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
      background: '#101010',
    },
    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
    },
    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
      background: "#555",
    },
    "& .MuiDataGrid-footerContainer": {
      justifyContent: "center",
    },
    '& .MuiToolbar-root': {
      color: 'white', 
      '& svg': {
        color: 'white',
      }
    }
  };

  const desktopStyles: SxProps<Theme> = {
    color: "common.white",
    height: "100%",
    border: "none",
    maxHeight: "100%",
    maxwidth: "100%",
    backgroundColor: "rgba(10, 10, 10, 0.50)",
    "& .MuiDataGrid-virtualScroller": {
      overflowX: "hidden",
      height: "100%",
    },
    "& .hiddenBet": {
      color: alpha(theme.palette.common.white, 0.3),
      backgroundColor: "black !important",
    },
    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
      width: "8px",
      height: 14,
      padding: 8,
    },
    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
      backgroundColor: alpha(theme.palette.common.white, 0.15),
      borderRadius: 4,
      borderTop: "3px transparent solid",
      borderBottom: "3px transparent solid",
      backgroundClip: "padding-box",
    },
    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
      backgroundColor: alpha(theme.palette.common.white, 0.35),
      borderRadius: 4,
      borderTop: "3px transparent solid",
      borderBottom: "3px transparent solid",
      backgroundClip: "padding-box",
    },
    "& .MuiDataGrid-sortIcon": {
      color: "common.white",
    },
    "& .MuiDataGrid-columnSeparator": {
      color: alpha(theme.palette.common.white, 0.3),
    },
    "& .MuiDataGrid-row:nth-of-type(even)": {
      backgroundColor: alpha('#101010', 0.5),
      "& .MuiDataGrid-row.Mui-hovered": {
        backgroundColor: alpha('#101010', 0.5),
      },
    },
    "& .MuiDataGrid-cellContent": {
      fontWeight: 400,
    },
    "& .MuiDataGrid-columnHeader": {
      backgroundColor: "rgba(10, 10, 10, 0.50)",
      color: alpha(theme.palette.common.white, 0.7),
      fontWeight: 700,
      fontSize: 12,
      padding: "10px 8px",
    },
    "& .MuiDataGrid-root": {
      border: "none !important",
      borderRadius: "10px",
    },
    "& .MuiDataGrid-main": {
      border: "none !important",
      borderRadius: "10px",
    },
    "& .osrating": {
      borderRadius: "6px 0px 0px 0px",
      background: "linear-gradient(90deg, #00723B 0%, #004E28 100%)",
      "& .MuiDataGrid-columnHeaderTitle": {
        color: "common.white",
        fontWeight: 700,
      },
    },
    "& .MuiDataGrid-cell": {
      borderBottomWidth: 0,
      paddingTop: "8px",
      paddingBottom: "8px",
      "&:last-of-type": {
        borderRightWidth: 0,
      },
    },
    "& .osratingCell": {
      padding: 0,
    },
    "& .ev": {
      borderRadius: "6px 6px 0px 0px",
      background: "linear-gradient(90deg, #004E28 0%, #002E18 100%)",
      "& .MuiDataGrid-columnHeaderTitle": {
        color: "common.white",
        fontWeight: 700,
      },
    },
    "& .evCell": {
      padding: 0,
    },
    "& .MuiDataGrid-row.Mui-hovered, .MuiDataGrid-row:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08) !important",
    },
    "& .MuiDataGrid-menuIcon": {
      "& button": {
        width: "1.5em",
        "& svg": {
          width: "1.5em",
        },
      },
    },
    '& .bet-row-hidden': {
      color: alpha(theme.palette.common.white, 0.5 * 0.87),
    },
  };

  const style = mobileView ? mobileStyles : desktopStyles;

  return (
    <Grid
      id="optimizer-grid"
      container
      item
      xs={12}
      rowSpacing={1}
      direction={"column"}
      justifyContent={"flex-start"}
      alignItems={"flex-start"}
      sx={{
        background: '#101010',
        borderRadius: "10px",
        margin: 0,
        "& #grid-wrapper, #filter-chips-wrapper": { paddingLeft: 0 },
      }}
    >
      <Grid
        id="first-row-grid"
        container
        item
        alignItems="flex-start"
        justifyContent={"space-between"}
        padding={"0px 8px 0px 8px !important"}
      >
        <Grid item container
          xs={10}
          alignItems={'center'}
          justifyContent={'flex-start'}
          columnSpacing={1}
        >
          <Grid
            item
            xs={4.5}
            sm={3.5}
            md={2.5}
            lg={2.5}
            sx={{
              background: '#101010',
              borderRadius: "6px",
            }}
          >
            <MatchupsMultiSelect
              matchups={matchups}
              filters={matchupsFilter?.map((x) => x.id)}
              AddMatchupFilter={(option) => dispatch(setMatchupsFilter(option))}
              RemoveMatchupFilter={(option) =>
                dispatch(removeMatchupsFilter(option))
              }
            />
          </Grid>
        </Grid>
        <Grid
          container
          item
          justifyContent={"center"}
          gap={10}
          sx={{
            background: '#101010',
            borderRadius: "6px",
            width: "40px",
            height: "40px",
          }}
        >
          <ToggleButton value="" onClick={() => getOutcomes()} sx={{ width: '40px', height: '40px', color: theme.palette.common.white }}>
            <RefreshIcon />
          </ToggleButton>
        </Grid>
      </Grid>
      <Grid id="filter-chips-wrapper" container item>
        <ScrollableChips options={offers} selectedOptions={offersFilter} setSelectedOptions={_setOfferFilters}></ScrollableChips>
      </Grid>
      <Grid
        id="grid-wrapper"
        container
        item
        sx={{
          "& .MuiDataGrid-main": {
            borderRadius: 0,
          },
          "& .MuiDataGrid-root": {
            borderRadius: 0,
            minHeight: "300px",
            maxHeight: '100%',
          },
          padding: 0,
        }}
      >
        <OSDataGrid
          data={gridData}
          defaultSort="desc"
          columns={createOptimizerDesktopColumns(
            getType,
            handleLegClick,
            userAuthenticated,
            true,
            exposureCounts,
            theme
          )}
          mobileColumns={createOptimizerMobileColumns(
            getType,
            handleLegClick,
            userAuthenticated,
            exposureCounts,
          )}
          isMobile={mobileView}
          //BDK FUTURE Hook up paging to server side request if performance becomes an issue
          tableProps={{
            pagination: true,
            paginationMode: "client",
            rowCount: gridData.length,
            paginationModel: paginationModel,
            onPaginationModelChange: (model) => {
              setPaginationModel({
                page: model.page,
                pageSize: model.pageSize,
              });
              window.scrollTo({ top: 0 });
            },
            slots: {
              noRowsOverlay: NoRowsOverlay,
              loadingOverlay: LinearProgress,
            },
          }}
          loading={loading}
          sxDesktop={style}
          sxMobile={style}
        />
      </Grid>
    </Grid>
  )
}

export default OutcomesTableWrapper